import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toastUtil } from '../utils/toast'
import { authApi, userApi, ApiErrorResponse } from '../api'
import { SocialProvider } from '../constants/social'
import { handleNaverCallback } from '../utils/naverAuth'
import { useAuthStore } from '../stores/authStore'

const NaverCallback: React.FC = () => {
  const navigate = useNavigate()
  const { setToken, setUserId, setProfileImage, getPostLoginPath } =
    useAuthStore()
  const [searchParams] = useSearchParams()
  const hasProcessed = useRef(false)
  // FCM 토큰 등록은 App.tsx 전역 핸들러가 전담 (여기서 덮어쓰면 전역 핸들러 파괴됨)
  const [fcmToken] = useState<string | null>(null)

  // (FCM 토큰 수신 핸들러는 App.tsx 전역에서 상시 관리 — 여기서 등록/해제하지 않는다)

  useEffect(() => {
    if (hasProcessed.current) {
      return
    }

    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const error = searchParams.get('error')
    const errorDescription = searchParams.get('error_description')

    if (error) {
      hasProcessed.current = true
      const errorMsg =
        errorDescription || '네이버 로그인 중 오류가 발생했습니다'
      if (!errorMsg.includes('취소')) {
        toastUtil.error(errorMsg)
      }
      navigate('/login')
      return
    }

    if (!code || !state) {
      hasProcessed.current = true
      toastUtil.error('네이버 로그인 인증 정보를 받을 수 없습니다')
      navigate('/login')
      return
    }

    hasProcessed.current = true

    handleNaverCallback(
      code,
      state,
      async (providerId: string) => {
        try {
          const response = await authApi.socialLogin({
            provider: SocialProvider.NAVER,
            provider_id: providerId,
          })

          if (response.token) {
            setToken(response.token)
          }

          // 사용자 프로필 가져와서 ID 및 프로필 이미지 저장
          let profileResponse: Awaited<ReturnType<typeof userApi.getProfile>> | null = null
          try {
            profileResponse = await userApi.getProfile()
            setUserId(profileResponse.user.id)
            setProfileImage(profileResponse.user.profile_image || null)
          } catch (error) {
            // 프로필 로드 실패 시 무시
          }

          // 로그인 성공 후 FCM 토큰 저장 (비동기)
          if (fcmToken) {
            try {
              await userApi.saveFcmToken(fcmToken)
            } catch (error) {
              // FCM 토큰 저장 실패는 로그만 남기고 계속 진행
              console.error('FCM 토큰 저장 실패:', error)
            }
          }

          navigate(
            getPostLoginPath(profileResponse?.user?.contact_consent ?? false)
          )
        } catch (error: any) {
          const apiError = error.response?.data as ApiErrorResponse
          const errorMessage =
            apiError?.message || '로그인 중 오류가 발생했습니다'

          if (errorMessage.includes('가입된 계정이 없습니다')) {
            const socialData = JSON.stringify({
              provider: SocialProvider.NAVER,
              provider_id: providerId,
            })
            const encodedData = btoa(socialData)
            // 가입 이력 없어도 이탈 방지: 에러 없이 바로 휴대폰 입력(→OTP→가입)으로
            navigate(`/register/phone?social=${encodedData}`)
          } else {
            toastUtil.error(errorMessage)
            navigate('/login')
          }
        }
      },
      (error: string) => {
        if (!error.includes('취소')) {
          toastUtil.error(error)
        }
        navigate('/login')
      }
    )
  }, [searchParams, navigate, fcmToken, setToken, setUserId, setProfileImage, getPostLoginPath])

  return (
    <div className="w-full h-full flex justify-center items-center">
      <div className="app-container py-40px px-16px h-full flex flex-col justify-center items-center">
        <div className="text-16px text-textColorText2">
          네이버 로그인 처리 중...
        </div>
      </div>
    </div>
  )
}

export default NaverCallback
